import {
  type ILabel,
  Constructor,
  INode,
  type IRenderContext,
  LabelStyleBase,
  Rect,
  Size,
  type Visual,
} from '@ardoq/yfiles';
import { ComponentGraphGroup, LabelExportSupport } from '@ardoq/graph';
import {
  type LabelStyleProperties,
  createLabelTextFallbackExtractor,
  createSingleLabelExporter,
} from '@ardoq/export';
import {
  measureNodeLabel,
  MODERNIZED_BLOCK_DIAGRAM_LINE_HEIGHT as LINE_HEIGHT,
} from '@ardoq/graph-labeling';
import { ensureHeaderLayout } from '../ensureHeaderLayout';
import { GroupLabelVisual } from './GroupLabelVisual';
import { wrapString } from 'tabview/blocks/misc/string';
import { MODERNIZED_BLOCK_DIAGRAM_LABEL_MAX_LINES as MAX_LINES } from 'yfilesExtensions/styles/modernized/consts';

import { createLabelStyle, rgbaToHex } from '../../shared/styleUtils';
import { colors } from '@ardoq/design-tokens';
import { readableColor } from 'polished';
import { MODERNIZED_BLOCK_DIAGRAM_EXPANDER_SIZE as EXPANDER_SIZE } from '../consts';
import { safeGetRepresentationDataFromINodeTag } from '../modernizedBlockDiagramRepresentationData';
import {
  getGraphTag,
  getGroupHeaders,
  getNodeFromLabel,
} from '../../shared/layoutUtils';

class GroupLabelStyle extends LabelStyleBase {
  static Instance = new GroupLabelStyle();

  public static getStyle(
    _context: IRenderContext,
    label: ILabel
  ): LabelStyleProperties | null {
    const node = getNodeFromLabel(label);
    if (!node) return null;

    const graphGroup = node.tag as ComponentGraphGroup | null;
    if (!graphGroup) return null;

    const { isTransparentized } = graphGroup;

    const stripeColor =
      graphGroup.isComponent() && graphGroup.getCssColorAttributes().fill;

    const representationData =
      graphGroup.isComponent() &&
      safeGetRepresentationDataFromINodeTag(graphGroup);

    const style = createLabelStyle(null, undefined, {
      isTransparentized,
      fill: rgbaToHex(stripeColor || colors.transparent0),
      textColor: stripeColor
        ? readableColor(stripeColor, colors.grey500, colors.white950)
        : colors.black950,
      textAlign: 'left',
      dominantBaseline: 'middle',
    });

    return { ...style, hasComponentRepresentation: !!representationData };
  }

  public static getLayout(context: IRenderContext, label: ILabel): Rect {
    const groupHeaders = getGroupHeaders(context);
    const { owner, text } = label;
    const node = owner as INode | null;
    if (!node) {
      return Rect.EMPTY;
    }
    const graphGroup = node?.tag as ComponentGraphGroup | null;
    if (!node || !graphGroup) {
      return Rect.EMPTY;
    }

    let wrappedMainLabel: string[];
    if (graphGroup.collapsed) {
      wrappedMainLabel = wrapString(
        text,
        label.layout.width,
        measureNodeLabel,
        MAX_LINES
      );
    } else {
      const groupHeader = groupHeaders
        ? ensureHeaderLayout(node, groupHeaders)
        : null;
      wrappedMainLabel = groupHeader ? groupHeader.wrappedMainLabel : [];
    }
    const labelHeight = wrappedMainLabel.length * LINE_HEIGHT;

    if (wrappedMainLabel.length === 0) {
      return Rect.EMPTY;
    }

    const { x, y, width } = node.layout;

    const height = Math.max(labelHeight, EXPANDER_SIZE + 2);

    return new Rect(x, y, width, height);
  }

  private constructor() {
    super();
  }

  protected override lookup(label: ILabel, type: Constructor): any {
    if (type === LabelExportSupport) {
      const provider: LabelExportSupport = {
        toJSON: createSingleLabelExporter(
          GroupLabelStyle.getLayout,
          GroupLabelStyle.getStyle,
          createLabelTextFallbackExtractor()
        ),
      };
      return provider;
    }
    return super.lookup(label, type);
  }

  protected override createVisual(
    context: IRenderContext,
    label: ILabel
  ): Visual | null {
    const graphTag = getGraphTag(context);
    const groupHeaders = graphTag?.groupHeaders || null;
    const {
      text,
      owner,
      layout: {
        bounds: { x, y, width },
      },
    } = label;
    if (!(owner instanceof INode)) {
      return null;
    }
    const graphGroup = owner?.tag as ComponentGraphGroup | null;
    if (!graphGroup) {
      return null;
    }
    const { isTransparentized } = graphGroup;

    const wrappedMainLabel =
      (groupHeaders &&
        ensureHeaderLayout(owner, groupHeaders)?.wrappedMainLabel) ??
      wrapString(text, width, measureNodeLabel, MAX_LINES);

    const stripeColor =
      (graphGroup.isComponent() && graphGroup.getCssColorAttributes().fill) ||
      null;
    const representationData =
      (graphGroup.isComponent() &&
        safeGetRepresentationDataFromINodeTag(graphGroup)) ||
      null;
    return new GroupLabelVisual(
      x,
      y,
      wrappedMainLabel,
      isTransparentized,
      stripeColor,
      representationData
    );
  }

  protected override getPreferredSize(_label: ILabel): Size {
    return Size.ZERO;
  }
}
export { GroupLabelStyle };
